import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  MAIL_STATUSES,
  orderSortFields,
  type MailStatus,
  type Order,
  type OrderListQuery,
} from '@faktorplus/shared';
import { useOrders, useResendOrder } from '../../api/orders';
import { useCurrentUser } from '../../auth/auth-provider';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { MailStatusPill } from '../../components/ui/status-pill';
import { SortableTableHeader } from '../../components/ui/sortable-table-header';
import { StatTile, StatTiles } from '../../components/ui/stat-tile';
import { AlertIcon, CheckIcon, ClockAlertIcon, InboxIcon } from '../../components/ui/icons';
import { OrderFilters, hasAnyOrderFilter } from './order-filters';
import { formatDate, formatQuantity, formatRelativeDay, plural } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './order-history-page.module.css';

const PAGE_SIZE = 25;

/**
 * What was ordered, and whether it left.
 *
 * A register rather than a stack of cards: the question people bring here is
 * "which of these did not go out" or "what did we order from that merchant in
 * July", and both are answered by sorting a column, not by scrolling. What was
 * actually ordered stays one click away — the row opens onto its own positions.
 *
 * Filters and sort live in the URL, so a view can be re-opened or sent on.
 */
export function OrderHistoryPage() {
  const user = useCurrentUser();
  const placedNow = usePlacedOrderId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(null);

  const filters = readFilters(searchParams);
  const { data, isLoading, error } = useOrders({ ...filters, pageSize: PAGE_SIZE });

  const orders = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const sortBy = filters.sortBy ?? 'placedAt';
  const sortDir = filters.sortDir ?? 'desc';
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    // Any filter change invalidates the current page number.
    if (!('page' in patch)) next.delete('page');

    setSearchParams(next, { replace: true });
  }

  function sort(column: string) {
    const nextSortDir = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    update({ sortBy: column, sortDir: nextSortDir });
  }

  return (
    <>
      <header className={pageStyles.header}>
        <h1 className={pageStyles.title}>
          {user.role === 'SITE_MANAGER' ? 'Moje zamówienia' : 'Zamówienia'}
        </h1>
        <span className={pageStyles.subtitle}>
          {total} {plural(total, ['zamówienie', 'zamówienia', 'zamówień'])}
          {orders.length > 0 && ` · strona ${page} z ${lastPage}`}
        </span>
        {user.role === 'SITE_MANAGER' && (
          <div className={pageStyles.actions}>
            <Link to="/zamowienia/nowe">
              <Button variant="primary">+ Złóż zamówienie</Button>
            </Link>
          </div>
        )}
      </header>

      {data?.summary && data.summary.total > 0 && (
        <StatTiles>
          <StatTile
            icon={<InboxIcon />}
            label="Zamówień"
            value={String(data.summary.total)}
          />
          <StatTile
            tone="ok"
            icon={<CheckIcon />}
            label="Wysłane"
            value={String(data.summary.sent)}
          />
          <StatTile
            tone={data.summary.pending > 0 ? 'warning' : 'default'}
            icon={<ClockAlertIcon />}
            label="W kolejce"
            value={String(data.summary.pending)}
          />
          <StatTile
            tone={data.summary.failed > 0 ? 'critical' : 'default'}
            icon={<AlertIcon />}
            label="Niewysłane"
            value={String(data.summary.failed)}
          />
        </StatTiles>
      )}

      {placedNow && (
        <div className={styles.accepted} role="status">
          <span aria-hidden="true">✓</span>
          <span>Zamówienie przyjęte. Wysyłamy je teraz — status zobaczysz na liście.</span>
        </div>
      )}

      <OrderFilters
        params={searchParams}
        onChange={update}
        onReset={() => setSearchParams({}, { replace: true })}
      />

      {error && <ErrorBox>Nie udało się pobrać zamówień. Odśwież stronę.</ErrorBox>}
      {isLoading && <LoadingBlock />}

      {!isLoading && !error && orders.length === 0 && (
        <EmptyState
          mark="∅"
          title="Brak zamówień"
          description={
            hasAnyOrderFilter(searchParams)
              ? 'Zmień filtry, aby zobaczyć więcej.'
              : 'Zamówienia, które złożysz, znajdziesz tutaj razem ze statusem wysyłki.'
          }
        />
      )}

      {orders.length > 0 && (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <SortableTableHeader label="Do kogo" column="recipient" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <SortableTableHeader label="Adres e-mail" column="email" activeColumn={sortBy} direction={sortDir} onSort={sort} className={pageStyles.hideOnPhone} />
                <SortableTableHeader label="Kiedy" column="placedAt" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <SortableTableHeader label="Budowa" column="project" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <SortableTableHeader label="Adres dostawy" column="city" activeColumn={sortBy} direction={sortDir} onSort={sort} className={pageStyles.hideOnPhone} />
                <SortableTableHeader label="Status" column="status" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <th aria-label="Akcje" />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <OrderRows
                  key={order.id}
                  order={order}
                  isNew={order.id === placedNow}
                  isOpen={order.id === openId}
                  onToggle={() => setOpenId(openId === order.id ? null : order.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div className={pageStyles.pagination}>
          <Button disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
            ← Nowsze
          </Button>
          <span>
            Strona {page} z {lastPage}
          </span>
          <span className={pageStyles.paginationSpacer} />
          <Button disabled={page >= lastPage} onClick={() => update({ page: String(page + 1) })}>
            Starsze →
          </Button>
        </div>
      )}
    </>
  );
}

interface OrderRowsProps {
  order: Order;
  isNew: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

/** The row itself plus, when opened, the positions it was placed for. */
function OrderRows({ order, isNew, isOpen, onToggle }: OrderRowsProps) {
  const resend = useResendOrder();
  const itemCount = order.items.length;

  return (
    <>
      <tr
        className={isNew ? styles.rowNew : undefined}
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
        aria-expanded={isOpen}
      >
        <td className={pageStyles.strong}>
          {order.vendorName ?? order.recipientEmail}
          <span className={styles.itemCount}>
            {' '}
            · {itemCount} {plural(itemCount, ['pozycja', 'pozycje', 'pozycji'])}
          </span>
        </td>
        <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>{order.recipientEmail}</td>
        <td>{formatDate(order.placedAt)}</td>
        {/* Site-less rows are the ones placed before the field existed. */}
        <td className={order.projectName ? undefined : pageStyles.muted}>
          {order.projectName ?? '—'}
        </td>
        <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
          {order.delivery.street}, {order.delivery.city}
        </td>
        <td>
          <MailStatusPill status={order.status} />
        </td>
        <td className={styles.actionCell}>
          {order.status === 'FAILED' && (
            <Button
              isLoading={resend.isPending}
              onClick={(event) => {
                event.stopPropagation();
                resend.mutate(order.id);
              }}
            >
              Wyślij ponownie
            </Button>
          )}
        </td>
      </tr>

      {isOpen && (
        <tr className={styles.detailRow}>
          <td colSpan={7}>
            <OrderDetail order={order} />
          </td>
        </tr>
      )}
    </>
  );
}

function OrderDetail({ order }: { order: Order }) {
  return (
    <div className={styles.detail}>
      <ul className={styles.items}>
        {order.items.map((item, index) => (
          <li key={`${item.name}-${index}`}>
            <span className={styles.itemName}>{item.name}</span>
            <span className={styles.itemQuantity}>
              {formatQuantity(item.quantity)} {item.unit}
            </span>
          </li>
        ))}
      </ul>

      <dl className={styles.facts}>
        <dt>Dostawa</dt>
        <dd>
          {order.delivery.street}, {order.delivery.postalCode} {order.delivery.city} · tel.{' '}
          {order.delivery.contactPhone}
        </dd>

        <dt>Złożył</dt>
        <dd>
          {order.placedByName} · {formatRelativeDay(order.placedAt)}
        </dd>

        <dt>Wysyłka</dt>
        <dd>
          {order.sentAt
            ? `Wysłane ${formatRelativeDay(order.sentAt)}`
            : order.status === 'FAILED'
              ? `Nie wysłano po ${order.attempts} ${plural(order.attempts, ['próbie', 'próbach', 'próbach'])}`
              : 'W kolejce do wysyłki'}
          {order.lastError && <span className={styles.failureDetail}>{order.lastError}</span>}
        </dd>

        {order.notes && (
          <>
            <dt>Uwagi</dt>
            <dd>{order.notes}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

/** Set by the order form when it hands over, so the fresh order is easy to spot. */
function usePlacedOrderId(): string | null {
  const location = useLocation();
  const state = location.state as { placedOrderId?: string } | null;
  return state?.placedOrderId ?? null;
}

function readFilters(params: URLSearchParams): Partial<OrderListQuery> {
  const status = params.get('status');
  const sortBy = params.get('sortBy');
  const sortDir = params.get('sortDir');

  return {
    ...(params.get('search')?.trim() && { search: params.get('search')!.trim() }),
    ...(isMailStatus(status) && { status }),
    ...(params.get('projectId') && { projectId: params.get('projectId')! }),
    ...(params.get('placedFrom') && { placedFrom: new Date(params.get('placedFrom')!) }),
    ...(params.get('placedTo') && { placedTo: new Date(params.get('placedTo')!) }),
    ...(isSortField(sortBy) && { sortBy }),
    ...((sortDir === 'asc' || sortDir === 'desc') && { sortDir }),
    page: Number(params.get('page')) || 1,
  };
}

function isMailStatus(value: string | null): value is MailStatus {
  return MAIL_STATUSES.includes(value as MailStatus);
}

function isSortField(value: string | null): value is OrderListQuery['sortBy'] {
  return orderSortFields.includes(value as OrderListQuery['sortBy']);
}
